import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Api from "../../Api/Api.json";
import Header from "../../components/Header/Header";
import Loading from "../../components/Loading";
import Error from "../../components/Error";

const Cardiology = () => {
  const [itemList, setItemList] = useState([]);
  const [mp3URL, setMP3URL] = useState('');
  const [imageURL, setImageURL] = useState('');
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [reportText, setReportText] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [abnormalStatus, setAbnormalStatus] = useState('normal'); // 'normal' or 'abnormal'
  const audioRef = useRef(null);
  const handlersRef = useRef({});

  // Fetch item list on component load
  useEffect(() => {
    setIsLoading(true);
    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.DOCTOR_API;
    const requestData = {
      task: 'getUnreportedPhonocardiogramScreen',
    };

    axios.post(url, requestData, headers)
      .then(response => {
        setIsLoading(false);
        if (response.data && response.data.length > 0) {
          // Store the full item objects as they contain patient_id
          setItemList(response.data);
        } else {
          setItemList([]);
        }
      })
      .catch(error => {
        setIsLoading(false);
        setIsError(true);
        setErrorMessage('Failed to load cardiology list. Please try again.');
        console.log(error);
      });
  }, []);

  const handleClick = async (item) => {
    // Stop previous audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setSelectedItem(item);
    setImageURL('');
    setMP3URL('');
    setReportText('');
    setSubmitMessage('');
    setAbnormalStatus('normal'); // Reset to normal when selecting new item
    
    // Extract pid and fileName from item
    const patientId = item.pid;
    const fileName = item.fileName;
    
    // Get image url
    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.PATIENT_API;
    
    // Get image URL
    var requestData = {
      task: 'getStethoWaveImg',
      patient_id: patientId,
      fileName: fileName
    };
    
    try {
      const imageResponse = await axios.post(url, requestData, headers);
      if (imageResponse.data && imageResponse.data.length > 0) {
        setImageURL(imageResponse.data);
      }
    } catch (error) {
      console.log('Error fetching image:', error);
    }
    
    // Get mp3 URL
    requestData = {
      task: 'getStethoDownloadURL',
      patient_id: patientId,
      fileName: fileName
    };
    
    try {
      const audioResponse = await axios.post(url, requestData, headers);
      if (audioResponse.data && audioResponse.data.length > 0) {
        setMP3URL(audioResponse.data);
      }
    } catch (error) {
      console.log('Error fetching audio:', error);
    }
  };

  // Initialize audio when mp3URL changes
  useEffect(() => {
    // Cleanup previous audio first
    if (audioRef.current && handlersRef.current) {
      audioRef.current.pause();
      if (handlersRef.current.handleLoadedMetadata) {
        audioRef.current.removeEventListener('loadedmetadata', handlersRef.current.handleLoadedMetadata);
      }
      if (handlersRef.current.handleTimeUpdate) {
        audioRef.current.removeEventListener('timeupdate', handlersRef.current.handleTimeUpdate);
      }
      if (handlersRef.current.handleEnded) {
        audioRef.current.removeEventListener('ended', handlersRef.current.handleEnded);
      }
      if (handlersRef.current.handleError) {
        audioRef.current.removeEventListener('error', handlersRef.current.handleError);
      }
      audioRef.current = null;
      handlersRef.current = {};
    }
    
    if (mp3URL) {
      audioRef.current = new Audio(mp3URL);
      
      // Define event handlers
      const handleLoadedMetadata = () => {
        if (audioRef.current) {
          setDuration(audioRef.current.duration);
        }
      };
      
      const handleTimeUpdate = () => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
      };
      
      const handleEnded = () => {
        setPlaying(false);
        setCurrentTime(0);
      };
      
      const handleError = (e) => {
        console.error('Audio error:', e);
        setPlaying(false);
      };
      
      // Store handlers for cleanup
      handlersRef.current = {
        handleLoadedMetadata,
        handleTimeUpdate,
        handleEnded,
        handleError
      };
      
      // Set up event listeners
      audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
      audioRef.current.addEventListener('ended', handleEnded);
      audioRef.current.addEventListener('error', handleError);
      
      // Cleanup function
      return () => {
        if (audioRef.current && handlersRef.current) {
          audioRef.current.removeEventListener('loadedmetadata', handlersRef.current.handleLoadedMetadata);
          audioRef.current.removeEventListener('timeupdate', handlersRef.current.handleTimeUpdate);
          audioRef.current.removeEventListener('ended', handlersRef.current.handleEnded);
          audioRef.current.removeEventListener('error', handlersRef.current.handleError);
          audioRef.current.pause();
          audioRef.current = null;
          handlersRef.current = {};
        }
      };
    }
  }, [mp3URL]);

  const togglePlay = async () => {
    if (!audioRef.current || !mp3URL) {
      return;
    }
    
    if (!playing) {
      try {
        await audioRef.current.play();
        setPlaying(true);
      } catch (error) {
        console.error('Error playing audio:', error);
        setPlaying(false);
      }
    } else {
      audioRef.current.pause();
      setPlaying(false);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmitReport = async () => {
    if (!selectedItem || !reportText.trim()) {
      setSubmitMessage('Please enter a report before submitting.');
      return;
    }

    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.DOCTOR_API;
    const requestData = {
      task: 'submitPhonocardiogramScreenReport',
      pid: selectedItem.pid,
      fileName: selectedItem.fileName,
      abnormal: abnormalStatus === 'abnormal' ? 1 : 0,
      report: reportText.trim()
    };

    try {
      const response = await axios.post(url, requestData, headers);
      if (response.status === 200) {
        // Remove the submitted item from the list
        setItemList(prevList => 
          prevList.filter(item => 
            !(item.pid === selectedItem.pid && item.fileName === selectedItem.fileName)
          )
        );
        setSubmitMessage('Submitted successfully.');
        setReportText('');
        setAbnormalStatus('normal');
        setSelectedItem(null);
        setImageURL('');
        setMP3URL('');
        setPlaying(false);
        setCurrentTime(0);
        setDuration(0);
      } else {
        setSubmitMessage('Didnot submit report, something went wrong.');
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      setSubmitMessage('Didnot submit report, something went wrong.');
    }
  };

  return (
    <>
      <Header pageTitle="Cardiology" />
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow-md p-6 mb-4 max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Cardiology</h1>
          
          {isLoading && <Loading />}
          {isError && <Error error={errorMessage} />}
          
          {!isLoading && !isError && (
            <CardiologyList
              itemList={itemList}
              onItemClick={handleClick}
              mp3URL={mp3URL}
              imageURL={imageURL}
              playing={playing}
              onTogglePlay={togglePlay}
              currentTime={currentTime}
              duration={duration}
              formatTime={formatTime}
              selectedItem={selectedItem}
              reportText={reportText}
              setReportText={setReportText}
              onSubmitReport={handleSubmitReport}
              submitMessage={submitMessage}
              abnormalStatus={abnormalStatus}
              setAbnormalStatus={setAbnormalStatus}
            />
          )}
        </div>
      </div>
    </>
  );
};

const CardiologyList = ({
  itemList,
  onItemClick,
  mp3URL,
  imageURL,
  playing,
  onTogglePlay,
  currentTime,
  duration,
  formatTime,
  selectedItem,
  reportText,
  setReportText,
  onSubmitReport,
  submitMessage,
  abnormalStatus,
  setAbnormalStatus
}) => {
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;
  
  const isItemSelected = (item) => {
    if (!selectedItem) return false;
    // Compare by reference or by pid and fileName
    if (typeof item === 'object' && typeof selectedItem === 'object') {
      return item === selectedItem || 
             (item.pid === selectedItem.pid && item.fileName === selectedItem.fileName);
    }
    return item === selectedItem;
  };
  
  const getItemDisplayText = (item) => {
    if (typeof item === 'string') {
      return item;
    }
    // Display fileName from the item
    return item.fileName || JSON.stringify(item);
  };
  
  return (
    <div className="mt-4">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Cardiology Records</h2>
      
      {imageURL !== "" && (
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-800 mb-2">Waveform Image:</h3>
          <img 
            src={imageURL} 
            alt="Cardiology waveform" 
            className="w-full h-auto border border-gray-300 rounded-lg" 
          />
        </div>
      )}
      
      {selectedItem && (
        <div className="mb-4 flex gap-4">
          {/* Audio Player - 1/4 width */}
          {mp3URL !== "" ? (
            <div className="w-1/4 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Play Audio:</h3>
              
              {/* Progress Bar */}
              <div className="mb-3">
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
              
              {/* Play/Pause Button */}
              <button 
                onClick={onTogglePlay}
                className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {playing ? 'Pause' : 'Play'}
              </button>
            </div>
          ) : (
            <div className="w-1/4"></div>
          )}
          
          {/* Report Textarea and Submit - 3/4 width */}
          <div className="flex-1 flex flex-col">
            {/* Radio Buttons */}
            <div className="mb-3">
              <label className="block text-lg font-medium text-gray-800 mb-2">Status:</label>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="abnormalStatus"
                    value="normal"
                    checked={abnormalStatus === 'normal'}
                    onChange={(e) => setAbnormalStatus(e.target.value)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-gray-700">Normal</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="abnormalStatus"
                    value="abnormal"
                    checked={abnormalStatus === 'abnormal'}
                    onChange={(e) => setAbnormalStatus(e.target.value)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-gray-700">Abnormal</span>
                </label>
              </div>
            </div>
            <div className="mb-2">
              <label className="block text-lg font-medium text-gray-800 mb-2">Report:</label>
              <textarea
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                placeholder="Enter your report here..."
                className="w-full h-48 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onSubmitReport}
                disabled={!reportText.trim()}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  reportText.trim()
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                }`}
              >
                Submit Report
              </button>
              {submitMessage && (
                <p className={`text-sm ${
                  submitMessage === 'Submitted successfully.' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {submitMessage}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {itemList.length === 0 ? (
        <p className="text-gray-500">No cardiology records found.</p>
      ) : (
        <ul className="space-y-2">
          {itemList.map((item, index) => (
            <li 
              key={index} 
              onClick={() => onItemClick(item)}
              className={`bg-gray-50 border border-gray-200 rounded-lg p-3 cursor-pointer hover:bg-gray-100 transition-colors ${
                isItemSelected(item) ? 'border-blue-500 bg-blue-50' : ''
              }`}
            >
              <p className="text-gray-700">{getItemDisplayText(item)}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Cardiology;

